import React, { useCallback, useEffect, useState } from "react";
import { useQuery, useQueryClient } from "react-query";
import { useMatch, useNavigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

import styled from "styled-components";

import AiCursor from "components/atoms/AiCursor";
import Icon from "components/atoms/Icon";
import { inlineCitationMarkdownComponents } from "components/atoms/InlineCitation";
import Alert from "components/atoms/alert/Alert";
import Button, { BUTTON_STYLES } from "components/atoms/button";
import SelectBox from "components/atoms/input/SelectBox";
import useLoading from "components/atoms/loading/useLoading";
import Text from "components/atoms/text/Text";

import AbstractModal from "components/page/modal/AbstractModal";

import { useSelectBoxOptions } from "hooks/queries/useOptions";
import { useNoteWithMarkdownCreate } from "hooks/queries/useNote";

import { getConvertedSearchPapers } from "utils/request/search";
import { toBibJson } from "utils/scienceon";
import { sseUrl } from "utils/urls";
import { uuidv4 } from "utils/uuid";

const Container = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;
    min-height: 0;
`;

const ContentsWrap = styled.div`
    display: flex;
    flex: 1 1 auto;
    margin: 24px 0 0 0;
    min-height: 0;
`;

const ContentsSection = styled.div`
    display: flex;
    flex: 1 1 auto;
    flex-direction: column;
    min-height: 0;
`;

const ItemView = styled.div`
    display: flex;
    flex: 1 1 auto;
    flex-direction: column;
    width: 100%;
    min-height: 0;
`;

const RelatedResearchView = styled.div`
    display: flex;
    flex: 1 1 auto;
    flex-direction: column;
    margin: 16px 0 0 0;
    width: 100%;
    min-height: 0;
    overflow: hidden;
`;

const TabSection = styled.div`
    display: flex;
    align-items: center;
`;

const TabButton = styled.button`
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;
    width: 120px;
    height: 40px;
    background-color: var(--color-White);
    border: Transparent;

    &:after {
        content: "";
        position: absolute;
        right: 0;
        bottom: 0;
        left: 0;
        width: 100%;
        height: 2px;
        background-color: ${(props) => (props.$active ? "var(--color-Key)" : "var(--color-DisabledInput)")};
    }
`;

const TabButtonText = styled(Text).attrs((props) => ({
    variant: "body-md-400",
    color: props.$active ? "var(--color-Key)" : "var(--color-DisabledText)",
}))``;

const TabViewSection = styled.div`
    display: flex;
    flex: 1 1 auto;
    flex-direction: column;
    margin: 16px 0 0 0;
    width: 100%;
    min-height: 0;
    overflow: hidden;
`;

const RelResearchView = styled.div`
    display: flex;
    flex: 1 1 auto;
    flex-direction: column;
    width: 100%;
    min-height: 0;
    overflow-y: hidden;
`;

const RelParagraphWrap = styled.div`
    flex: 1 1 auto;
    min-height: 0;
    width: 100%;
    overflow-y: auto;
    overflow-x: hidden;
    overscroll-behavior: contain;
    scrollbar-gutter: stable;
    padding-right: 4px;
`;

const RelParagraph = styled.div`
    margin: 0;
    padding: 4px;
    width: 100%;
    min-height: 100%;
    border: transparent;
    border-radius: 4px;
    background-color: var(--color-White);
    text-align: justify;
    line-height: 1.4;
    font-size: 13px;
    font-weight: 400;
    color: var(--color-SubBlack);
    resize: none;
    white-space: pre-wrap;

    h1,
    h2,
    h3 {
        margin: 12px 0 8px;
        font-weight: 700;
        line-height: 1.35;
    }

    p,
    ul,
    ol,
    blockquote,
    table {
        margin: 0 0 10px;
    }

    ul,
    ol {
        padding-left: 24px;
    }

    table {
        border-collapse: collapse;
        width: 100%;
    }

    th,
    td {
        border: 1px solid var(--color-Line);
        padding: 6px 8px;
        text-align: left;
    }

    &:focus {
        outline: none;
        background-color: var(--color-Base1);
    }

    &:disabled {
        cursor: text;
    }
`;

const ReferenceView = styled.div`
    display: flex;
    flex: 1 1 auto;
    flex-direction: column;
    position: relative;
    width: 100%;
    min-height: 0;
`;

const ButtonSection = styled.div`
    display: flex;
    align-items: center;
`;

const ReferenceListWrap = styled.div`
    display: flex;
    flex: 1 1 auto;
    flex-direction: column;
    width: 100%;
    min-height: 0;
    overflow-y: auto;
    overflow-x: hidden;
    overscroll-behavior: contain;
    scrollbar-gutter: stable;
    padding-right: 4px;
`;

const RefListItemWrap = styled.div`
    padding: 8px 0;
    width: 100%;
    border-bottom: dashed 1px var(--color-Line);
`;

const AnalysisView = styled.div`
    display: flex;
    flex: 1 1 auto;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    width: 100%;
    min-height: 0;
`;

const AnalysisDescText = styled(Text).attrs({
    variant: "body-md-400",
    margin: "0 0 20px 0",
})``;

const RefCitationText = styled(Text).attrs({
    variant: "body-md-400",
    color: "var(--color-Grey1)",
})``;

const SummaryFooter = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    height: 32px;
`;

function ReferenceSummaryModal(props) {
    const match = useMatch("/:space_uuid/*");
    const navigate = useNavigate();
    const space_uuid = match?.params?.space_uuid ?? "";
    const noteWithMarkdownCreateMutation = useNoteWithMarkdownCreate();
    const optionsQuery = useSelectBoxOptions();

    const [tabActive, setTabActive] = useState("summary");
    const [language, setLanguage] = useState("ko");
    const [languageOptions, setLanguageOptions] = useState([]);
    const [styledText, setStyledText] = useState([]);
    const [summaryText, setSummaryText] = useState("");
    const { setLoading } = useLoading();

    useEffect(() => {
        if (optionsQuery.data) {
            setLanguageOptions(optionsQuery.data.env_ai_language);
        }
    }, [optionsQuery.data]);

    const convertToBibText = useCallback(
        (papers) => {
            const references = papers.map((paper) => {
                return toBibJson(paper);
            });

            getConvertedSearchPapers(space_uuid, references).then((res) => setStyledText(res));
        },
        [space_uuid],
    );

    const buildSummarySources = useCallback(() => {
        return props.papers.list.map((paper, index) => {
            const isSavedReference = !!paper?.id && !!paper?.bib_json;
            const bibJson = isSavedReference ? paper.bib_json : toBibJson(paper);
            if (isSavedReference) {
                return {
                    index: index + 1,
                    reference_id: paper.id,
                    title: bibJson.title || paper.display_name || "",
                    authors: bibJson.author || "",
                    year: bibJson.year || "",
                    abstract: bibJson.abstract || "",
                };
            }
            return {
                index: index + 1,
                title: paper?.title || bibJson.title || "",
                authors: paper?.author || bibJson.author || "",
                year: bibJson.year || "",
                abstract: paper?.abstract || bibJson.abstract || "",
                bib_json: bibJson,
            };
        });
    }, [props.papers]);

    useEffect(() => {
        if (props.papers.type === "searchPapers" && props.papers.list.length > 0) {
            convertToBibText(props.papers.list);
        } else if (props.papers.type === "referenceList" && props.papers.list.length > 0) {
            setStyledText(
                props.papers.list.map((reference) => ({
                    citation_key: reference.bib_json.citation_key,
                    styled_text: reference.styled_text[0],
                })),
            );
        }
    }, [convertToBibText, props.papers]);

    const addNote = () => {
        const note = {
            markdown_content: summaryText,
            sources: buildSummarySources(),
        };
        noteWithMarkdownCreateMutation.mutate(note, {
            onSuccess: (data) => {
                props.exitModal();
                navigate(`/${space_uuid}/note/${data.note_no}`);
            },
        });
    };

    //region: 논문 요약 SSE 처리
    const [sseRequestId, setSseRequestId] = useState(null);

    const queryClient = useQueryClient();
    const sseReferenceSummarizeQueryKey = "sseReferenceSummarize";

    const sseReferenceSummarizeQuery = useQuery({
        queryKey: [sseReferenceSummarizeQueryKey],
        queryFn: async ({ signal }) => {
            try {
                setLoading(true);

                const request_id = uuidv4();
                setSseRequestId(request_id);

                const response = await fetch(sseUrl(sseReferenceSummarizeQueryKey), {
                    signal,
                    method: "POST",
                    headers: {
                        Authorization: `Token ${localStorage.getItem("token")}`,
                        "Content-Type": "application/json",
                        "X-Request-ID": request_id,
                    },
                    body: JSON.stringify({
                        sources: buildSummarySources(),
                        language: language,
                    }),
                });
                setLoading(false);

                if (!response.ok) {
                    throw new Error("Stream connection failed");
                }

                const reader = response.body?.getReader();
                if (!reader) {
                    throw new Error("Stream reader not available");
                }

                const decoder = new TextDecoder();
                while (true) {
                    const { value, done } = await reader.read();

                    if (done) {
                        break;
                    }

                    const dataRecv = decoder.decode(value, { stream: true });
                    setSummaryText((prev) => {
                        return prev + dataRecv;
                    });
                }
            } catch (error) {
                throw error;
            }
        },
        enabled: false,
        retry: false,
        onError: (error) => {
            console.log("error", error);
            Alert("warn", "에러", "요약문을 생성하지 못했습니다.");
        },
    });

    const startSummary = () => {
        setSummaryText("");
        sseReferenceSummarizeQuery.refetch();
    };

    const stopSummary = async () => {
        queryClient.cancelQueries({ queryKey: [sseReferenceSummarizeQueryKey] });
        await fetch(sseUrl(sseReferenceSummarizeQueryKey), {
            method: "PUT",
            headers: {
                Authorization: `Token ${localStorage.getItem("token")}`,
                "Content-Type": "application/json",
                "X-Request-ID": sseRequestId,
            },
            body: JSON.stringify({}),
        });
    };

    const summaryFooter =
        tabActive === "summary" && summaryText ? (
            <SummaryFooter>
                {sseReferenceSummarizeQuery.isFetching ? (
                    <Button
                        margin={"0 8px 0 0"}
                        width={"120px"}
                        height={"32px"}
                        bgColor={"var(--color-Button1)"}
                        hoverBgColor={"var(--color-ButtonHover1)"}
                        fontColor={"var(--color-White)"}
                        buttonText={"작성 중지"}
                        onClick={() => {
                            stopSummary();
                        }}
                    >
                        <Icon name={"stop"} color={"var(--color-White)"} />
                    </Button>
                ) : (
                    <>
                        <Button
                            onlyText
                            {...BUTTON_STYLES.actionPrimary}
                            margin={"0 8px 0 0"}
                            height={"32px"}
                            width={"120px"}
                            buttonText={"연구노트로 추가"}
                            onClick={() => {
                                addNote();
                            }}
                        />
                        <Button
                            onlyText
                            margin={"0 8px 0 0"}
                            height={"32px"}
                            width={"80px"}
                            bgColor={"var(--color-Button1)"}
                            hoverBgColor={"var(--color-ButtonHover1)"}
                            fontColor={"var(--color-White)"}
                            buttonText={"다시 작성"}
                            onClick={() => {
                                startSummary();
                            }}
                        />
                    </>
                )}
                <Button
                    {...BUTTON_STYLES.modalSecondary}
                    buttonText={"닫기"}
                    onClick={() => {
                        stopSummary();
                        props.exitModal();
                    }}
                />
            </SummaryFooter>
        ) : null;

    return (
        <AbstractModal
            modalTitle="요약하기"
            width={800}
            height={820}
            exitModal={props.exitModal}
            footer={summaryFooter}
        >
            <Container>
                <ContentsWrap>
                    <ContentsSection>
                        <TabSection>
                            <TabButton onClick={() => setTabActive("summary")} $active={tabActive === "summary"}>
                                <TabButtonText $active={tabActive === "summary"}>요약</TabButtonText>
                            </TabButton>
                            <TabButton onClick={() => setTabActive("papers")} $active={tabActive === "papers"}>
                                <TabButtonText $active={tabActive === "papers"}>출처</TabButtonText>
                            </TabButton>
                        </TabSection>
                        {tabActive === "summary" ? (
                            summaryText ? (
                                <ItemView key={"related-view"} $active={true}>
                                    <RelatedResearchView>
                                        <RelResearchView>
                                            <RelParagraphWrap>
                                                <RelParagraph>
                                                    <ReactMarkdown
                                                        remarkPlugins={[remarkGfm]}
                                                        components={inlineCitationMarkdownComponents}
                                                    >
                                                        {summaryText}
                                                    </ReactMarkdown>
                                                    {sseReferenceSummarizeQuery.isFetching && <AiCursor />}
                                                </RelParagraph>
                                            </RelParagraphWrap>
                                        </RelResearchView>
                                    </RelatedResearchView>
                                </ItemView>
                            ) : (
                                <AnalysisView>
                                    <AnalysisDescText>요약을 작성할 언어를 선택해주세요.</AnalysisDescText>
                                    <ButtonSection>
                                        <SelectBox
                                            width="100px"
                                            height="32px"
                                            placeholder="언어 선택"
                                            items={languageOptions}
                                            value={language}
                                            onChange={(e) => setLanguage(e)}
                                        />
                                        <Button
                                            onlyText
                                            {...BUTTON_STYLES.actionPrimary}
                                            margin={"0 0 0 8px"}
                                            width={"80px"}
                                            height={"32px"}
                                            buttonText={"요약하기"}
                                            onClick={startSummary}
                                            disabled={sseReferenceSummarizeQuery.isFetching}
                                        />
                                    </ButtonSection>
                                </AnalysisView>
                            )
                        ) : (
                            <ItemView>
                                <TabViewSection>
                                    <ReferenceView>
                                        <ReferenceListWrap>
                                            {styledText.map((text) => (
                                                <RefListItemWrap key={text.citation_key}>
                                                    <RefCitationText>{text.styled_text}</RefCitationText>
                                                </RefListItemWrap>
                                            ))}
                                        </ReferenceListWrap>
                                    </ReferenceView>
                                </TabViewSection>
                            </ItemView>
                        )}
                    </ContentsSection>
                </ContentsWrap>
            </Container>
        </AbstractModal>
    );
}

export default ReferenceSummaryModal;
